import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type {FloorplanListHalfboardDTO} from "crqlar-react-common/dist/models/floorplan/floorplanListHalfboard.dto";
import type {FloorplanReservationItemDTO} from "crqlar-react-common/dist/models/floorplan/floorplanReservationItem.dto";
import type {Reservation, ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import type {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {FloorplanService} from "crqlar-react-common/dist/services/floorplan/floorplan.service";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {
    endOfDay,
    formatDateLocalISO,
    startOfDay,
} from "crqlar-react-common/dist/utils/date.util";
import {type ReactNode, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQuery, useQueryClient} from "react-query";
import ReadSidebarActiveItem from "../../floorPlan/v2/read/ReadSidebarActiveItem";
import ReadSidebarHalfboardItem from "../../floorPlan/v2/read/ReadSidebarHalfboardItem";
import ReadSidebarUnassignedItem from "../../floorPlan/v2/read/ReadSidebarUnassignedItem";
import {
    groupFloorplanReservations,
    groupHalfboardProfiles,
} from "../../floorPlan/v2/utils/reservation-utils";
import ReservationDialog from "../../reservationDialog/ReservationDialog";
import {
    ReservationRailSearch,
    type ReservationRailSearchState,
} from "../search/ReservationRailSearch";
import type {ReservationRailSearchController} from "../search/useReservationRailSearch";

const FLOORPLAN_SEARCH_QUERY_KEY = "floorplanRailSearch";

interface FloorplanSearchResults {
    assigned: FloorplanReservationItemDTO[];
    unassigned: FloorplanReservationItemDTO[];
    arrivals: FloorplanListHalfboardDTO[];
}

interface FloorplanSearchPanelProps {
    restaurant: Resource;
    date: Date;
    statuses: ReservationStatus[];
    search: ReservationRailSearchController;
}

const SearchGroup = ({title, children}: {title: string; children: ReactNode}) => (
    <Box component="section">
        <Typography
            variant="overline"
            color="text.secondary"
            sx={{display: "block", px: 2, pt: 2, pb: 0.5, fontWeight: 700}}
        >
            {title}
        </Typography>
        {children}
    </Box>
);

export function FloorplanSearchPanel({restaurant, date, statuses, search}: FloorplanSearchPanelProps) {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const organization = useOrganization();
    const [selectedReservation, setSelectedReservation] = useState<Reservation>();
    const {query, setQuery, debouncedQuery} = search;
    const restaurantId = restaurant.id ?? "";
    const dayKey = formatDateLocalISO(date) ?? "";
    const normalizedStatuses = useMemo(() => [...statuses].sort(), [statuses]);
    const statusKey = normalizedStatuses.join("-");

    const searchQuery = useQuery<FloorplanSearchResults>({
        queryKey: [FLOORPLAN_SEARCH_QUERY_KEY, restaurantId, dayKey, statusKey, debouncedQuery],
        enabled: Boolean(restaurantId && debouncedQuery),
        keepPreviousData: true,
        queryFn: async () => {
            const limit = {
                offset: 0,
                limit: 1000,
                sort: [{field: "expectedStartDate", direction: "ASC" as const}],
            };
            const timespan = {
                start: startOfDay(date),
                end: endOfDay(date),
            };
            const [assigned, unassigned, arrivals] = await Promise.all([
                FloorplanService.getAssignedReservations({
                    restaurantId,
                    limit,
                    timespan,
                    query: debouncedQuery,
                    status: normalizedStatuses,
                }),
                FloorplanService.getUnassignedReservations({
                    restaurantId,
                    limit,
                    timespan,
                    query: debouncedQuery,
                    status: normalizedStatuses,
                }),
                FloorplanService.getHalfboards({
                    restaurantId,
                    limit,
                    timespan,
                    query: debouncedQuery,
                }),
            ]);
            const toTimestamp = (value: Date | string | number | undefined) => {
                if (value == null) return Number.MAX_SAFE_INTEGER;
                const timestamp = new Date(value).getTime();
                return Number.isNaN(timestamp) ? Number.MAX_SAFE_INTEGER : timestamp;
            };

            return {
                assigned: groupFloorplanReservations(assigned).sort((left, right) =>
                    toTimestamp(left.restaurantReservation?.expectedStartDate) -
                    toTimestamp(right.restaurantReservation?.expectedStartDate),
                ),
                unassigned: groupFloorplanReservations(unassigned).sort((left, right) =>
                    toTimestamp(left.restaurantReservation?.expectedStartDate) -
                    toTimestamp(right.restaurantReservation?.expectedStartDate),
                ),
                arrivals: groupHalfboardProfiles(
                    arrivals,
                    organization?.agenciesWithIndividualReservations,
                ).sort((left, right) =>
                    toTimestamp(left.accommodationReservation?.expectedStartDate) -
                    toTimestamp(right.accommodationReservation?.expectedStartDate),
                ),
            };
        },
    });

    const results = searchQuery.data;
    const resultCount = (results?.assigned.length ?? 0)
        + (results?.unassigned.length ?? 0)
        + (results?.arrivals.length ?? 0);
    let searchState: ReservationRailSearchState;
    if (!query.trim() || !debouncedQuery) {
        searchState = "idle";
    } else if (searchQuery.isLoading) {
        searchState = "loading";
    } else if (searchQuery.isError) {
        searchState = "error";
    } else if (searchQuery.isFetching) {
        searchState = "refreshing";
    } else if (resultCount === 0) {
        searchState = "empty";
    } else {
        searchState = "results";
    }

    const invalidateFloorplanData = () => {
        void queryClient.invalidateQueries(["floorplanAssigned"]);
        void queryClient.invalidateQueries(["floorplanUnassigned"]);
        void queryClient.invalidateQueries(["floorplanHalfboardProfiles"]);
    };

    const handleSaved = () => {
        setSelectedReservation(undefined);
        void searchQuery.refetch();
        invalidateFloorplanData();
    };

    return (
        <>
            <ReservationRailSearch
                query={query}
                onQueryChange={setQuery}
                state={searchState}
                idleMessage={t("Search reservations by guest, room, or table")}
                emptyMessage={t('No results for "{{query}}"', {query: debouncedQuery})}
                onRetry={() => void searchQuery.refetch()}
            >
                {close => <Stack>
                    {!!results?.assigned.length && (
                        <SearchGroup title={t("Reservations")}>
                            {results.assigned.map(reservation => (
                                <ReadSidebarActiveItem
                                    key={reservation.reservationId}
                                    reservation={reservation}
                                    onClickLocate={close}
                                    onClickEdit={value => {
                                        close();
                                        setSelectedReservation(value);
                                    }}
                                />
                            ))}
                        </SearchGroup>
                    )}

                    {!!results?.unassigned.length && (
                        <SearchGroup title={t("Unassigned")}>
                            {results.unassigned.map(reservation => (
                                <ReadSidebarUnassignedItem
                                    key={reservation.reservationId}
                                    reservation={reservation}
                                    draggable={false}
                                    onClickEdit={value => {
                                        close();
                                        setSelectedReservation(value);
                                    }}
                                />
                            ))}
                        </SearchGroup>
                    )}

                    {!!results?.arrivals.length && (
                        <SearchGroup title={t("Arrivals")}>
                            {results.arrivals.map(arrival => (
                                <ReadSidebarHalfboardItem
                                    key={arrival.accommodationReservation?.id ?? arrival.profileIds?.[0]}
                                    halfboard={arrival}
                                    draggable={false}
                                    onClickEdit={value => {
                                        close();
                                        setSelectedReservation(value);
                                    }}
                                />
                            ))}
                        </SearchGroup>
                    )}
                </Stack>}
            </ReservationRailSearch>

            <ReservationDialog
                open={Boolean(selectedReservation)}
                reservation={selectedReservation}
                onClose={() => setSelectedReservation(undefined)}
                onSave={handleSaved}
                onChangeReservation={setSelectedReservation}
            />
        </>
    );
}
